import React from "react";
import { Badge, darken, Typography } from "@mui/material";
import {
  ExerciseAssetDto,
  ExerciseTagDto,
} from "@growth-machine-llc/stridist-api-client";
import makeStyles from "@mui/styles/makeStyles";
import TagChip from "./tags/TagChip";
import clsx from "clsx";
import { ExerciseTagMultiSelect } from "./ExerciseTagMultiSelect";
import { exerciseLibraryStyles } from "./exerciseLibraryStyles";
import { useTheme } from "@mui/styles";
import { ExerciseSelectedTags } from "./ExerciseSelectedTags";

export interface TagBadgeProps {
  selectedTags: ExerciseTagDto[];
  open?: boolean;
  setOpen?: (val: boolean) => void;
  anchorEl?: HTMLElement | null;
  setAnchorEl?: (el: HTMLElement | null) => void;
  showDashWhenEmpty?: boolean;
  classes?: Partial<Record<"badge" | "category", string>>;
  readonly?: boolean;
  showOnlySelected?: boolean;
}

interface ExerciseTagBadgeProps extends TagBadgeProps {
  exercise: ExerciseAssetDto;
}

export const ExerciseTagBadge: React.FC<ExerciseTagBadgeProps> = ({
  selectedTags = [],
  open,
  setOpen,
  anchorEl,
  setAnchorEl,
  exercise,
  showDashWhenEmpty = true,
  classes,
  readonly = false,
  showOnlySelected = false,
}) => {
  const s = exerciseLibraryStyles();
  const theme = useTheme();
  const first = selectedTags && selectedTags[0];

  const handleClick = (e: React.MouseEvent<HTMLElement>) => {
    setOpen && setOpen(true);
    setAnchorEl && setAnchorEl(e.currentTarget);
    e.stopPropagation();
    e.preventDefault();
  };

  return (
    <>
      {first ? (
        <Badge
          badgeContent={
            selectedTags.length > 1 ? `+${selectedTags.length - 1}` : null
          }
          invisible={selectedTags.length <= 1}
          className={classes?.badge}
          sx={{
            "& .MuiBadge-badge": {
              backgroundColor: darken(first.primaryColor, 0.2),
              color: theme.palette.common.white,
            },
          }}
        >
          <TagChip
            primaryColor={first.primaryColor}
            text={first.title}
            className={clsx(classes?.category, s.badgeCategoryTag)}
            onClick={handleClick}
          />
        </Badge>
      ) : showDashWhenEmpty ? (
        <Typography
          onClick={handleClick}
          sx={{
            cursor: "pointer",
            paddingY: 2,
          }}
        >
          -
        </Typography>
      ) : null}

      {showOnlySelected && selectedTags.length > 0 ? (
        <ExerciseSelectedTags
          open={open}
          setOpen={setOpen}
          anchorEl={anchorEl}
          selectedTags={selectedTags}
          onClose={() => {
            setOpen && setOpen(false);
            setAnchorEl && setAnchorEl(null);
          }}
        />
      ) : (
        <ExerciseTagMultiSelect
          open={open}
          setOpen={setOpen}
          anchorEl={anchorEl}
          exercise={exercise}
          onClose={() => {
            setOpen && setOpen(false);
            setAnchorEl && setAnchorEl(null);
          }}
          initialSelected={selectedTags}
          showButton={false}
          readonly={readonly}
        />
      )}
    </>
  );
};
